import React, { useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { trackFormSubmission } from '../utils/analytics';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    _gotcha: '' // Honeypot field for spam protection
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to Formspree
      const response = await fetch('https://formspree.io/f/xblnavpw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company || 'Not provided',
          message: formData.message,
          language: t.language === 'et' ? 'Estonian' : 'English',
          _subject: `New Contact from 13Labs Website: ${formData.name}`,
          _replyto: formData.email,
          _gotcha: formData._gotcha // Honeypot field
        })
      });

      if (response.ok) {
        // Track successful form submission
        trackFormSubmission('contact_form');
        
        toast.success(
          t.language === 'et' 
            ? 'Sõnum saadetud! Vastame 24 tunni jooksul.' 
            : 'Message sent successfully! We\'ll get back to you within 24 hours.'
        );
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: '',
          _gotcha: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(
        t.language === 'et' 
          ? 'Midagi läks valesti. Palun proovige uuesti või võtke meiega otse ühendust.' 
          : 'Something went wrong. Please try again or contact us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-6 bg-black">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="w-14 h-14 md:w-16 md:h-16 bg-[#D6CE15]/10 rounded-xl flex items-center justify-center mx-auto mb-4 md:mb-6 border border-[#D6CE15]/30">
            <Mail className="text-[#D6CE15]" size={28} />
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 px-2">
            {t.contact.title.split(' ')[0]} <span className="text-[#D6CE15] neon-text">{t.contact.title.split(' ').slice(1).join(' ')}</span>
          </h1>
          <p className="text-base md:text-xl text-gray-400 px-4">
            {t.contact.subtitle}
          </p>
        </div>

        {/* Contact Form */}
        <div className="bg-[#0a0a0a] border border-[#D6CE15]/20 rounded-2xl p-6 md:p-8 lg:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                {t.contact.form.name} {t.contact.form.required}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder={t.contact.form.namePlaceholder}
                className="w-full bg-black border-2 border-[#D6CE15]/20 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#D6CE15] focus:ring-2 focus:ring-[#D6CE15]/20 transition-all"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {t.contact.form.email} {t.contact.form.required}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder={t.contact.form.emailPlaceholder}
                className="w-full bg-black border-2 border-[#D6CE15]/20 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#D6CE15] focus:ring-2 focus:ring-[#D6CE15]/20 transition-all"
              />
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                {t.contact.form.company}
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder={t.contact.form.companyPlaceholder}
                className="w-full bg-black border-2 border-[#D6CE15]/20 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#D6CE15] focus:ring-2 focus:ring-[#D6CE15]/20 transition-all"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                {t.contact.form.message} {t.contact.form.required}
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows="6"
                value={formData.message}
                onChange={handleChange}
                placeholder={t.contact.form.messagePlaceholder}
                className="w-full bg-black border-2 border-[#D6CE15]/20 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#D6CE15] focus:ring-2 focus:ring-[#D6CE15]/20 transition-all resize-none"
              />
            </div>

            {/* Honeypot field - hidden from users, catches spam bots */}
            <input
              type="text"
              name="_gotcha"
              value={formData._gotcha}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
              style={{ position: 'absolute', left: '-9999px' }}
              aria-hidden="true"
            />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center space-x-2 px-8 py-4 bg-[#D6CE15] text-black rounded-lg font-semibold text-lg hover:bg-[#A4A71E] transition-all duration-200 hover:shadow-2xl hover:shadow-[#D6CE15]/50 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 group"
            >
              <span>{isSubmitting ? t.contact.form.sending : t.contact.form.submit}</span>
              {!isSubmitting && <Send className="group-hover:translate-x-1 transition-transform" size={20} />}
            </button>
          </form>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-500">
            {t.contact.info}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;